// Design System Shadow Tokens
// Consistent elevation and shadow system

// Base shadow colors
const shadowColors = {
  light: {
    primary: 'rgba(0, 0, 0, 0.1)',
    secondary: 'rgba(0, 0, 0, 0.06)',
    colored: 'rgba(15, 58, 97, 0.15)', // Primary color shadow for light theme
  },
  dark: {
    primary: 'rgba(0, 0, 0, 0.3)',
    secondary: 'rgba(0, 0, 0, 0.2)',
    colored: 'rgba(0, 0, 0, 0.4)', // Darker shadow for dark theme
  },
};

// Elevation scale (0-24)
export const elevations = {
  0: 'none',
  1: {
    light: `0 1px 3px ${shadowColors.light.primary}, 0 1px 2px ${shadowColors.light.secondary}`,
    dark: `0 1px 3px ${shadowColors.dark.primary}, 0 1px 2px ${shadowColors.dark.secondary}`,
  },
  2: {
    light: `0 1px 5px ${shadowColors.light.primary}, 0 2px 2px ${shadowColors.light.secondary}`,
    dark: `0 1px 5px ${shadowColors.dark.primary}, 0 2px 2px ${shadowColors.dark.secondary}`,
  },
  3: {
    light: `0 1px 8px ${shadowColors.light.primary}, 0 3px 4px ${shadowColors.light.secondary}`,
    dark: `0 1px 8px ${shadowColors.dark.primary}, 0 3px 4px ${shadowColors.dark.secondary}`,
  },
  4: {
    light: `0 2px 4px ${shadowColors.light.primary}, 0 4px 5px ${shadowColors.light.secondary}`,
    dark: `0 2px 4px ${shadowColors.dark.primary}, 0 4px 5px ${shadowColors.dark.secondary}`,
  },
  6: {
    light: `0 3px 5px ${shadowColors.light.primary}, 0 6px 10px ${shadowColors.light.secondary}`,
    dark: `0 3px 5px ${shadowColors.dark.primary}, 0 6px 10px ${shadowColors.dark.secondary}`,
  },
  8: {
    light: `0 5px 5px ${shadowColors.light.primary}, 0 8px 10px ${shadowColors.light.secondary}`,
    dark: `0 5px 5px ${shadowColors.dark.primary}, 0 8px 10px ${shadowColors.dark.secondary}`,
  },
  12: {
    light: `0 7px 8px ${shadowColors.light.primary}, 0 12px 17px ${shadowColors.light.secondary}`,
    dark: `0 7px 8px ${shadowColors.dark.primary}, 0 12px 17px ${shadowColors.dark.secondary}`,
  },
  16: {
    light: `0 8px 10px ${shadowColors.light.primary}, 0 16px 24px ${shadowColors.light.secondary}`,
    dark: `0 8px 10px ${shadowColors.dark.primary}, 0 16px 24px ${shadowColors.dark.secondary}`,
  },
  20: {
    light: `0 10px 13px ${shadowColors.light.primary}, 0 20px 31px ${shadowColors.light.secondary}`,
    dark: `0 10px 13px ${shadowColors.dark.primary}, 0 20px 31px ${shadowColors.dark.secondary}`,
  },
  24: {
    light: `0 11px 15px ${shadowColors.light.primary}, 0 24px 38px ${shadowColors.light.secondary}`,
    dark: `0 11px 15px ${shadowColors.dark.primary}, 0 24px 38px ${shadowColors.dark.secondary}`,
  },
};

// Semantic shadow tokens
export const shadows = {
  // Component shadows
  component: {
    card: {
      light: elevations[2].light,
      dark: elevations[2].dark,
    },
    cardHover: {
      light: elevations[8].light,
      dark: elevations[8].dark,
    },
    button: {
      light: elevations[2].light,
      dark: elevations[2].dark,
    },
    buttonHover: {
      light: elevations[4].light,
      dark: elevations[4].dark,
    },
    input: {
      light: elevations[1].light,
      dark: elevations[1].dark,
    },
    inputFocus: {
      light: elevations[3].light,
      dark: elevations[3].dark,
    },
  },
  
  // Layout shadows
  layout: {
    header: {
      light: elevations[4].light,
      dark: elevations[4].dark,
    },
    sidebar: {
      light: elevations[8].light,
      dark: elevations[8].dark,
    },
    modal: {
      light: elevations[24].light,
      dark: elevations[24].dark,
    },
    dropdown: {
      light: elevations[8].light,
      dark: elevations[8].dark,
    },
    tooltip: {
      light: elevations[6].light,
      dark: elevations[6].dark,
    },
  },
  
  // Special effects
  effects: {
    // Glow effects for interactive elements
    glow: {
      primary: {
        light: `0 0 0 3px rgba(15, 58, 97, 0.1)`,
        dark: `0 0 0 3px rgba(75, 127, 199, 0.3)`,
      },
      secondary: {
        light: `0 0 0 3px rgba(242, 197, 38, 0.2)`,
        dark: `0 0 0 3px rgba(242, 197, 38, 0.3)`,
      },
      error: {
        light: `0 0 0 3px rgba(239, 68, 68, 0.1)`,
        dark: `0 0 0 3px rgba(248, 113, 113, 0.3)`,
      },
      success: {
        light: `0 0 0 3px rgba(34, 197, 94, 0.1)`,
        dark: `0 0 0 3px rgba(74, 222, 128, 0.3)`,
      },
    },
    
    // Inner shadows
    inset: {
      light: `inset 0 2px 4px 0 ${shadowColors.light.secondary}`,
      dark: `inset 0 2px 4px 0 ${shadowColors.dark.secondary}`,
    },
    
    // Colored shadows for brand elements
    colored: {
      primary: {
        light: `0 4px 14px 0 ${shadowColors.light.colored}`,
        dark: `0 4px 14px 0 ${shadowColors.dark.colored}`,
      },
      secondary: {
        light: `0 4px 14px 0 rgba(242, 197, 38, 0.15)`,
        dark: `0 4px 14px 0 rgba(242, 197, 38, 0.25)`,
      },
    },
  },
};

// Shadow utilities
export const shadowUtils = {
  // Get shadow for specific theme and elevation
  getElevation: (level, theme = 'light') => {
    if (level === 0) return 'none';
    return elevations[level]?.[theme] || elevations[2][theme];
  },
  
  // Get component shadow
  getComponentShadow: (component, state = 'default', theme = 'light') => {
    const componentShadows = shadows.component[component];
    if (!componentShadows) return shadows.component.card[theme];
    
    const stateKey = state === 'hover' ? `${component}Hover` : component;
    return shadows.component[stateKey]?.[theme] || componentShadows[theme];
  },
  
  // Get layout shadow
  getLayoutShadow: (layout, theme = 'light') => {
    return shadows.layout[layout]?.[theme] || shadows.layout.dropdown[theme];
  },
  
  // Get focus shadow
  getFocusShadow: (variant = 'primary', theme = 'light') => {
    return shadows.effects.glow[variant]?.[theme] || shadows.effects.glow.primary[theme];
  },
  
  // Create custom shadow with opacity
  createShadow: (x, y, blur, spread, color, opacity = 1) => {
    if (color.includes('rgba')) {
      return `${x}px ${y}px ${blur}px ${spread}px ${color.replace(/[\d\.]+\)$/g, `${opacity})`)}`;
    }
    return `${x}px ${y}px ${blur}px ${spread}px ${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
  },
};

export default {
  elevations,
  semantic: shadows,
  utils: shadowUtils,
};